// src/components/ProgramOutcomesSection.jsx
"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import LearnerBenefitsCard from "./LearnerBenefitsCard";
import ProgramModules from "./Modules";
import { programOutcomes } from "@/data/programOutcomes";
import ProgramAudienceSection from "./Audience";
import CertificationSection from "./Certificate";
import BonusesSection from "./Bonus";
import ProgramFaqs from "./Faq";
// import MentorsSection from "./Mentors";

export default function ProgramOutcomesSection({ mentors }) {
  const [expanded, setExpanded] = useState(false);
  const [programData, setProgramData] = useState(null);
  const pathname = usePathname();

  useEffect(() => {
    // Extract the slug based on the path pattern
    let slug;
    const pathParts = pathname.split('/').filter(part => part);
    
    // Check if it's a workshop path
    if (pathParts[0] === 'workshops') {
      // For workshop paths, use format "workshops/[slug]"
      slug = `workshops/${pathParts[1]}`;
    } else {
      // For regular paths, just use the last part as the slug
      slug = pathParts[pathParts.length - 1];
    }

    // Find matching program outcome data
    const matchingProgram = programOutcomes.find(
      (program) => program.slug === slug
    );

    if (matchingProgram) {
      setProgramData(matchingProgram);
    } else {
      // Fallback to first program if no match found
      setProgramData(programOutcomes.length > 0 ? programOutcomes[0] : null);
    }
  }, [pathname]);

  if (!programData) {
    return (
      <div className="w-full py-8 text-center">Loading program details...</div>
    );
  }

  // Limit outcomes to first 2 when not expanded
  const visibleOutcomes = expanded
    ? programData.outcomes
    : programData.outcomes.slice(0, 2);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <section className="w-full bg-white py-16">
      <div className="w-[90%] md:w-[80%] mx-auto">
        <h2 className="text-[#002726] text-3xl md:text-4xl font-medium mb-8">
          Key Outcomes
        </h2>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left Column - Outcomes and Modules */}
          <div className="w-full lg:w-3/5">
            {/* Program Outcomes */}
            <p className="text-[#002726] text-base mb-8">
              {programData.introduction}
            </p>

            <ul className="list-none space-y-4 mb-8">
              {visibleOutcomes.map((outcome, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-[#002726] mr-3 text-xl">•</span>
                  <span className="text-[#002726] text-base">{outcome}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={toggleExpanded}
              className="flex items-center text-[#002726] font-medium mb-8"
            >
              {expanded ? "Show Less" : "Read More"}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`ml-2 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="#002726"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {expanded && (
              <>
                <h3 className="text-[#002726] text-xl font-medium mb-4">
                  Tools you will get to learn:
                </h3>
                <ul className="list-none space-y-2 mb-8">
                  {programData.tools.map((tool, index) => (
                    <li key={index} className="text-[#002726] text-base">
                      {tool}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Program Modules */}
            <ProgramModules />
            <ProgramAudienceSection />
            {/* <MentorsSection mentors={mentors} /> */}
            <CertificationSection />
            <BonusesSection />
            <ProgramFaqs />
          </div>

          {/* Right Column - Benefits Card */}
          <div className="w-full lg:w-2/5 relative hidden md:block">
            <div className="lg:sticky lg:top-30">
              <LearnerBenefitsCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}